import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  fetchReviews, 
  fetchReviewStats, 
  fetchGMBLocations, 
  fetchServices,
  fetchReviewById,
  createReviewResponse,
  updateReviewResponse,
  autoTagReviews
} from '@/utils/reviewsApi'
import type { ReviewsQuery, ReviewFilters, ReviewSort } from '@/types/reviews'

// Query keys for react-query
export const reviewsKeys = {
  all: ['reviews'] as const,
  lists: () => [...reviewsKeys.all, 'list'] as const,
  list: (query: ReviewsQuery) => [...reviewsKeys.lists(), query] as const,
  details: () => [...reviewsKeys.all, 'detail'] as const,
  detail: (id: string) => [...reviewsKeys.details(), id] as const,
  stats: () => [...reviewsKeys.all, 'stats'] as const,
  locations: () => ['gmb-locations'] as const,
  services: () => ['services'] as const,
}

/**
 * Hook for fetching reviews with filtering, sorting, and pagination
 */
export function useReviews(query: ReviewsQuery = {}) {
  return useQuery({
    queryKey: reviewsKeys.list(query),
    queryFn: () => fetchReviews(query),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  })
}

/**
 * Hook for fetching review statistics
 */
export function useReviewStats() {
  return useQuery({
    queryKey: reviewsKeys.stats(),
    queryFn: fetchReviewStats,
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

/**
 * Hook for fetching GMB locations
 */
export function useGMBLocations() {
  return useQuery({
    queryKey: reviewsKeys.locations(),
    queryFn: fetchGMBLocations,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

/**
 * Hook for fetching services
 */
export function useServices() {
  return useQuery({
    queryKey: reviewsKeys.services(),
    queryFn: fetchServices,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

/**
 * Hook for fetching a single review
 */
export function useReview(id: string) {
  return useQuery({
    queryKey: reviewsKeys.detail(id),
    queryFn: () => fetchReviewById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook for creating review responses
 */
export function useCreateReviewResponse() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({
      reviewId,
      responseText,
      publishToGoogle = false
    }: {
      reviewId: string
      responseText: string
      publishToGoogle?: boolean
    }) => createReviewResponse(reviewId, responseText, publishToGoogle),
    onSuccess: (data, variables) => {
      // Invalidate reviews queries to refresh data
      queryClient.invalidateQueries({ queryKey: reviewsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: reviewsKeys.stats() })
      queryClient.invalidateQueries({ queryKey: reviewsKeys.detail(variables.reviewId) })
    },
  })
}

/**
 * Hook for updating review responses
 */
export function useUpdateReviewResponse() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({
      responseId,
      responseText,
      publishToGoogle = false
    }: {
      responseId: string
      responseText: string
      publishToGoogle?: boolean
    }) => updateReviewResponse(responseId, responseText, publishToGoogle),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: reviewsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: reviewsKeys.stats() })
    },
  })
}

/**
 * Hook for auto-tagging reviews
 */
export function useAutoTagReviews() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (reviewIds?: string[]) => autoTagReviews(reviewIds),
    onSuccess: () => {
      // Invalidate reviews queries to show updated tags
      queryClient.invalidateQueries({ queryKey: reviewsKeys.lists() })
    },
  })
}